import React, { useState, useCallback } from 'react';
import {
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { uploadApprovedQaToRag } from '../utils/questionnaireApi';

type SnackbarState =
  | { open: true; severity: 'success' | 'error'; message: string }
  | { open: false };

function formatAxiosError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => (typeof d === 'object' && d && 'msg' in d ? String((d as { msg: string }).msg) : String(d))).join('; ');
    }
    if (err.response?.status === 403) return 'You do not have permission to update the knowledge base.';
    if (err.response?.status === 401) return 'Session expired. Please sign in again.';
  }
  return 'Failed to update knowledge base. Please try again. Ask OneSec team for help.';
}

export type UpdateKnowledgeBaseButtonProps = {
  variant?: 'contained' | 'outlined' | 'text';
  sx?: Record<string, unknown>;
};

const UpdateKnowledgeBaseButton: React.FC<UpdateKnowledgeBaseButtonProps> = ({ variant = 'outlined', sx }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false });

  const runUpload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await uploadApprovedQaToRag({
        delete_previous_day: true,
        test_mode_seconds: null,
      });
      if (data.success) {
        const msg =
          data.message?.trim() ||
          `Knowledge base updated: ${data.uploaded_count} Q&A pair(s) imported (${data.total_count} approved total).`;
        setSnackbar({ open: true, severity: 'success', message: msg });
        setDialogOpen(false);
      } else {
        const errMsg = data.errors?.length ? data.errors.join('; ') : data.message || 'Import finished with errors.';
        setSnackbar({ open: true, severity: 'error', message: errMsg });
        setDialogOpen(false);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: formatAxiosError(err),
      });
      setDialogOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => (prev.open ? { open: false } : prev));
  };

  return (
    <>
      <Button variant={variant} onClick={() => setDialogOpen(true)} disabled={loading} sx={sx}>
        Update knowledge base
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (loading) return;
          setDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="update-kb-dialog-title"
      >
        <DialogTitle id="update-kb-dialog-title">Update knowledge base</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            This imports approved question-and-answers into the OneSec RAG knowledge base.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.6 }}>
            Continue only if you intend to refresh the knowledge base.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={loading} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => void runUpload()} variant="contained" disabled={loading}>
            {loading ? 'Updating…' : 'Confirm update'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.open && snackbar.severity === 'success' ? 6000 : 8000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snackbar.open ? (
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default UpdateKnowledgeBaseButton;
